import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Card } from '@/design-system/ui/Card';
import { Button } from '@/design-system/ui/Button';
import { Segmented } from '@/design-system/ui/Controls';
import { Input } from '@/design-system/ui/Field';
import { Icon } from '@/design-system/ui/Icon';
import { cn } from '@/design-system/ui/cn';
import { EmptyState, ErrorState, ListSkeleton } from '@/design-system/ui/States';
import { toEnglishDigits, toPersianDigits } from '@/core/format/numbers';
import { isValidMobile } from '@/core/validation/validators';
import { useSEO } from '@/core/seo/useSEO';
import { pageLd } from '@/core/seo/jsonld';
import { SITE_URL } from '@/core/config/content';
import { faqCategories } from '@/features/content/data/faq';
import { useLoadByCode, useLoadsByMobile } from '../api/loads-api';
import { HeroWaves } from '@/features/trips/components/HeroWaves';
import { LoadDetails } from '../components/LoadDetails';
import { TrackProgress } from '../components/TrackProgress';
import { loadRole, loadTone, type Load } from '../model/schemas';

type Mode = 'code' | 'mobile';

const TONE_DOT = { success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger', info: 'bg-turquoise' } as const;

const TABS: { value: Mode; label: string; icon: 'search' | 'phone' }[] = [
  { value: 'code', label: 'کد رهگیری', icon: 'search' },
  { value: 'mobile', label: 'شماره موبایل', icon: 'phone' },
];

/**
 * Public (no login) tracking page — the target of the SMS link and of the old site's
 * `/?mode=code&q=…` / `/?mode=mobile&q=…` links (those are redirected here).
 * URL state: `?mode=code|mobile&q=<value>`; the older `?code=<value>` still works.
 */
export default function TrackLoadPage() {
  const [params, setParams] = useSearchParams();
  const mode: Mode = params.get('mode') === 'mobile' ? 'mobile' : 'code';
  const rawQuery = params.get('q') ?? (mode === 'code' ? params.get('code') : null);
  const query = rawQuery?.trim() ? toEnglishDigits(rawQuery.trim()) : null;

  const [input, setInput] = useState(query ?? '');
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Keep the field in sync when the URL changes from outside (back button, footer link…).
  const [prevSyncKey, setPrevSyncKey] = useState(() => `${mode}:${query ?? ''}`);
  const syncKey = `${mode}:${query ?? ''}`;
  if (prevSyncKey !== syncKey) {
    setPrevSyncKey(syncKey);
    setInput(query ?? '');
    setSelectedId(null);
  }

  const byCode = useLoadByCode(mode === 'code' ? query : null);
  const byMobile = useLoadsByMobile(mode === 'mobile' && query && isValidMobile(query) ? query : null);

  const selected = mode === 'mobile' ? byMobile.data?.find((l) => (l.id || l.code) + '' === selectedId) ?? null : null;
  const detail: Load | null = mode === 'code' ? byCode.data ?? null : selected;

  // Result pages hold a person's shipment / phone number: keep them out of search and AI indexes,
  // and never put the phone number into the title or canonical.
  useSEO(
    detail
      ? { title: `مرسوله ${detail.destination} — کد ${detail.code}`, description: 'وضعیت لحظه‌ای مرسوله در بلینو.', canonical: '/track', noindex: true }
      : query
        ? { title: 'نتیجه رهگیری مرسوله', description: 'نتیجه رهگیری مرسوله در بلینو.', canonical: '/track', noindex: true }
        : {
            title: 'رهگیری مرسوله با کد رهگیری یا شماره موبایل',
            description: 'وضعیت لحظه‌ای مرسوله و بار خود را در بلینو با کد رهگیری یا شماره موبایل فرستنده و گیرنده پیگیری کنید؛ بدون نیاز به ورود به حساب.',
            canonical: '/track',
            jsonLd: pageLd({
              name: 'رهگیری مرسوله بلینو',
              description: 'پیگیری لحظه‌ای وضعیت مرسوله با کد رهگیری یا شماره موبایل',
              path: '/track',
              extra: { about: { '@id': `${SITE_URL}/#service-shipment-tracking` } },
            }),
          },
  );


  const setMode = (m: Mode) => {
    setError('');
    setParams({ mode: m }, { replace: true });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = toEnglishDigits(input.trim());
    if (!v) return setError(mode === 'code' ? 'لطفاً کد رهگیری را وارد کنید' : 'لطفاً شماره موبایل را وارد کنید');
    if (mode === 'mobile' && !isValidMobile(v)) return setError('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود');
    setError('');
    setParams({ mode, q: v });
  };

  const isMobile = mode === 'mobile';
  const active = isMobile ? byMobile : byCode;

  return (
    <div className="pb-10">
      <section className="relative isolate overflow-hidden text-white [background:linear-gradient(225deg,#3d6f96_0%,#2e7a92_48%,#1d8c88_100%)]" aria-labelledby="track-title">
        <div className="pointer-events-none absolute -start-20 top-4 -z-10 size-80 animate-orb rounded-full bg-[radial-gradient(circle,rgb(255_228_146/0.28),transparent_68%)]" aria-hidden />
        <HeroWaves className="pointer-events-none absolute -z-10 max-w-none" />
        <div className="mx-auto max-w-2xl px-4 pb-24 pt-10 text-center">
          <span className="mx-auto mb-4 grid size-16 animate-hero-float place-items-center rounded-[20px] bg-white/15 text-white shadow-[0_12px_30px_-8px_rgb(4_15_27/0.5)] ring-1 ring-white/30 backdrop-blur-md">
            <Icon name="box" size={30} />
          </span>
          <h1 id="track-title" className="animate-rise text-balance text-3xl font-extrabold leading-snug md:text-4xl">رهگیری مرسوله بلینو</h1>
          <p className="mx-auto mt-2 max-w-sm animate-rise text-sm leading-7 text-white/85 [animation-delay:80ms] sm:text-base">با کد مرسوله یا شماره موبایل، فوری وضعیت بار خود را پیگیری کنید</p>
        </div>
      </section>

      <div className="mx-auto -mt-14 flex max-w-2xl flex-col gap-5 px-4">
      <Card className="animate-rise overflow-hidden shadow-floating [animation-delay:100ms]">
        <div className="p-3 pb-0 sm:p-4 sm:pb-0">
          <Segmented<Mode> ariaLabel="روش جستجو" value={mode} onChange={setMode} options={TABS} />
        </div>
        <form onSubmit={submit} noValidate className="flex flex-col gap-3 p-4 sm:p-5">
          <Input
            key={mode}
            label={isMobile ? 'شماره موبایل' : 'کد مرسوله'}
            placeholder={isMobile ? 'مثال: 09121234567' : 'مثال: 05042702001'}
            hint={isMobile && !error ? 'تمام بارهای مرتبط با این شماره نمایش داده می‌شود' : undefined}
            inputMode="numeric"
            dir="ltr"
            className="text-start"
            maxLength={isMobile ? 11 : undefined}
            autoComplete={isMobile ? 'tel' : 'off'}
            value={input}
            error={error || undefined}
            onChange={(e) => {
              const v = toEnglishDigits(e.target.value);
              setInput(isMobile ? v.replace(/\D/g, '') : v);
              setError('');
            }}
          />
          <Button type="submit" size="lg" fullWidth loading={Boolean(query) && active.isFetching && !error} icon={<Icon name="search" size={20} />}>
            جستجوی بار
          </Button>
        </form>
      </Card>

      <Results
        mode={mode}
        query={query}
        selected={detail}
        onBack={isMobile && selected ? () => setSelectedId(null) : undefined}
        onSelect={(l) => { setSelectedId(String(l.id || l.code)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        code={byCode}
        mobile={byMobile}
      />

      {!query && (
        <section aria-labelledby="track-help" className="mt-4">
          <h2 id="track-help" className="mb-3 text-lg font-bold">راهنمای رهگیری مرسوله بلینو</h2>
          <dl className="flex flex-col gap-4">
            {faqCategories.find((c) => c.id === 'tracking')?.items.map((i) => (
              <div key={i.q}>
                <dt className="font-bold text-navy">{i.q}</dt>
                <dd className="mt-1 text-sm leading-loose text-ink-2">{i.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      </div>
    </div>
  );
}

function Results({ mode, query, selected, onBack, onSelect, code, mobile }: {
  mode: Mode;
  query: string | null;
  selected: Load | null;
  onBack: (() => void) | undefined;
  onSelect: (l: Load) => void;
  code: ReturnType<typeof useLoadByCode>;
  mobile: ReturnType<typeof useLoadsByMobile>;
}) {
  if (!query) {
    return <EmptyState icon="search" title="کد رهگیری یا شماره موبایل خود را وارد کنید" description="وضعیت لحظه‌ای مرسوله شما همین‌جا نمایش داده می‌شود." />;
  }
  const q = mode === 'code' ? code : mobile;
  if (mode === 'mobile' && !isValidMobile(query)) return <EmptyState icon="phone" title="شماره موبایل معتبر نیست" description="شماره باید ۱۱ رقم باشد و با ۰۹ شروع شود." />;
  if (q.isPending) return <ListSkeleton rows={2} height="h-40" />;
  if (q.isError) return <ErrorState message={q.error.message} onRetry={() => q.refetch()} />;

  if (selected) {
    return (
      <div className="flex flex-col gap-3">
        {onBack && (
          <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1 text-sm font-bold text-navy hover:underline">
            <Icon name="back" size={16} />
            بازگشت به لیست
          </button>
        )}
        <Card className="animate-rise overflow-hidden shadow-floating">
          <div className="relative isolate overflow-hidden px-4 pb-5 pt-4 text-white [background:linear-gradient(225deg,#3d6f96_0%,#2e7a92_48%,#1d8c88_100%)] sm:px-5">
            <div className="pointer-events-none absolute -end-16 -top-16 -z-10 size-56 rounded-full bg-[radial-gradient(circle,rgb(255_228_146/0.25),transparent_68%)]" aria-hidden />
            <div className="mb-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-white/75">مقصد مرسوله</p>
                <h2 className="truncate text-xl font-extrabold">{selected.destination || '—'}</h2>
                <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-xs ring-1 ring-white/20">
                  <Icon name="ticket" size={13} />
                  کد: {toPersianDigits(selected.code)}
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-navy-deep shadow-card">
                <span className={cn('size-2 rounded-full', TONE_DOT[loadTone(selected)])} aria-hidden />
                {selected.statusLabel}
              </span>
            </div>
            <TrackProgress history={selected.history} />
          </div>
          <div className="p-4 sm:p-5">
            <LoadDetails load={selected} />
          </div>
        </Card>
      </div>
    );
  }

  if (mode === 'code') {
    return <EmptyState icon="search" title="مرسوله‌ای با این کد پیدا نشد" description={`کد ${toPersianDigits(query)} را بررسی کنید.`} />;
  }

  const list = mobile.data ?? [];
  if (list.length === 0) return <EmptyState icon="search" title="مرسوله‌ای برای این شماره پیدا نشد" description={`شمارهٔ ${toPersianDigits(query)} را بررسی کنید.`} />;

  return (
    <section aria-label="نتایج جستجو" className="flex flex-col gap-3">
      <p className="text-sm text-ink-2">{toPersianDigits(list.length)} مرسوله برای شماره {toPersianDigits(query)} یافت شد</p>
      <ul className="flex flex-col gap-3">
        {list.map((l, i) => {
          const role = loadRole(l, query);
          const tone = loadTone(l);
          return (
            <li key={l.id || l.code} className="animate-rise" style={{ animationDelay: `${Math.min(i, 6) * 60}ms` }}>
              <Card interactive className="overflow-hidden">
                <button type="button" className="group relative flex w-full items-center gap-4 p-4 ps-6 text-start" onClick={() => onSelect(l)}>
                  <span className={cn('absolute inset-y-0 start-0 w-1.5', TONE_DOT[tone])} aria-hidden />
                  <span className="grid size-12 shrink-0 place-items-center rounded-[14px] bg-primary-light text-navy transition duration-200 group-hover:scale-105 group-hover:bg-cta group-hover:text-white group-hover:shadow-glow"><Icon name="box" size={22} /></span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <span className="flex flex-wrap items-center justify-between gap-2">
                      <span className="truncate font-bold">مرسوله {l.destination || '—'}</span>
                      <Badge tone={tone}>{l.statusLabel}</Badge>
                    </span>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-2">
                      <span className="rounded-md bg-fill px-2 py-0.5 font-medium">کد: {toPersianDigits(l.code)}</span>
                      {l.packages.length > 0 && <span>{toPersianDigits(l.packages.length)} بسته</span>}
                      {role && <span className="font-bold text-action">{role === 'sender' ? 'فرستنده' : 'گیرنده'}</span>}
                    </span>
                    <span className="mt-0.5 flex gap-1" aria-hidden>
                      {l.history.map((st, k) => (
                        <span key={k} className={cn('h-1 flex-1 rounded-full', st.done ? (tone === 'danger' ? 'bg-danger' : 'bg-turquoise') : 'bg-line')} />
                      ))}
                    </span>
                  </span>
                  <Icon name="chevronLeft" size={18} className="shrink-0 text-ink-3 transition group-hover:-translate-x-1 group-hover:text-action" />
                </button>
              </Card>
            </li>
          );
        })}
      </ul>
      <p className="text-center text-xs text-ink-2">روی هر مرسوله بزنید تا جزئیات را ببینید</p>
    </section>
  );
}
